import type { CSSProperties, ReactElement } from 'react';

import { HelpLabel } from './InfoTip';

/* A state, rendered as a state. Shared because more than one screen shows one.
 *
 * `tone` is a semantic name, not a colour, and that is the whole point of the component. A screen
 * asking for `tone="unknown"` cannot accidentally get the same treatment as `tone="ok"` — which is
 * the failure this codebase already came close to shipping, where a PII verdict the classifier could
 * not produce would have rendered like one a human confirmed.
 *
 * `title` is required for the tones that need explaining. "Unknown" alone tells a reviewer nothing;
 * "PII status unknown — the classifier did not complete" tells them what to do.
 *
 * ---------------------------------------------------------------------------------------------
 * **Retreated 2026-08-24: a tinted pill with a dot, replacing the outlined chip.** Three signals
 * per state rather than one — the ground tint, the dot, and the label's words — because the outline
 * carried the whole state in a 1px line, which is the first thing lost to a low-contrast display or
 * a photocopier, and it made six tones read as six near-identical rectangles.
 *
 * The **dot is hollow for `unknown`**, matching `Indicator`. That is the load-bearing detail rather
 * than the tint: it means absence survives greyscale and colour-blindness, so a reader who cannot
 * separate the hues can still separate "we could not measure this" from every measured state. Both
 * components draw it the same way so they cannot come to disagree about how absence looks.
 *
 * Styling moved from inline to `.lq-badge` in `system.css`. The two per-tone colours are passed as
 * custom properties rather than as a class per tone: one rule, six data points, and a new tone
 * cannot render unstyled because the type makes the map total.
 */

export type BadgeTone = 'ok' | 'info' | 'warn' | 'unknown' | 'danger' | 'neutral';

export interface StatusBadgeProps {
  readonly tone: BadgeTone;
  readonly label: string;
  /** Hover and assistive text. What the state *means*, not a repeat of the label. */
  readonly title?: string;
  /** What the state means, behind an "i" beside the badge — reachable by keyboard and touch, which
   * `title` is not. For a badge standing alone (a detail page, a progress panel); in a table, put
   * the explanation on the column header instead, or every row grows an icon. */
  readonly help?: string | undefined;
}

interface Paint {
  /** The dot and the label. Measured against `tint`, not against a card — a badge's text sits on
   * its own tinted ground, and that is usually the binding contrast case. The lowest of the six is
   * 4.53:1. */
  readonly ink: string;
  readonly tint: string;
}

/* `satisfies Record<BadgeTone, …>` rather than an annotation, so adding a seventh tone to the union
 * is a compile error here rather than a badge that renders with the fallback colour. That fallback
 * would be the *neutral* one — the quietest of the six — so an unmapped danger state would lose its
 * red silently, which is the failure mode this codebase has recorded on a `default:` branch before. */
const PAINT = {
  ok: { ink: 'var(--status-ok)', tint: 'var(--ok-bg)' },
  info: { ink: 'var(--status-info)', tint: 'var(--info-bg)' },
  warn: { ink: 'var(--status-warn)', tint: 'var(--warn-bg)' },
  /* Its own hue, used nowhere else in the product. "We could not tell" is not a point on a
   * red-amber-green severity scale, so it does not borrow one — see tokens.css. */
  unknown: { ink: 'var(--status-unknown)', tint: 'var(--un-bg)' },
  danger: { ink: 'var(--status-error)', tint: 'var(--err-bg)' },
  /* Neutral shares the unknown tint and takes muted ink. They are deliberately *not* the same
   * badge: neutral is a state with no verdict attached (a draft, a count), unknown is a verdict the
   * platform could not reach — and only the second one gets the hollow dot. */
  neutral: { ink: 'var(--text-muted)', tint: 'var(--un-bg)' },
} satisfies Record<BadgeTone, Paint>;

export function StatusBadge({
  tone,
  label,
  title,
  help,
}: StatusBadgeProps): ReactElement {
  const paint = PAINT[tone];

  return (
    <HelpLabel subject={label} help={help} inline>
      <span
        className="lq-badge"
        title={title}
        style={{ '--badge-ink': paint.ink, '--badge-tint': paint.tint } as CSSProperties}
      >
        <span
          className={
            tone === 'unknown' ? 'lq-badge__dot lq-badge__dot--hollow' : 'lq-badge__dot'
          }
          /* Decorative: the label beside it already carries the state in words, so announcing the dot
           * would read the state twice to a screen reader. */
          aria-hidden="true"
        />
        {label}
      </span>
    </HelpLabel>
  );
}
